import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Navigation, Car, Users, Info, ArrowRight } from 'lucide-react';
import { API } from '../utils/api';

const DEFAULT_HOURS = [
  { day: "Lundi", isOpen: false, open: "", close: "" },
  { day: "Mardi", isOpen: true, open: "09:00", close: "18:30" },
  { day: "Mercredi", isOpen: true, open: "09:00", close: "18:30" },
  { day: "Jeudi", isOpen: true, open: "09:00", close: "18:30" },
  { day: "Vendredi", isOpen: true, open: "09:00", close: "18:30" },
  { day: "Samedi", isOpen: true, open: "09:00", close: "17:30" },
  { day: "Dimanche", isOpen: false, open: "", close: "" }
];

const formatTime = (t) => {
  if (!t) return '';
  return t.replace(':30', 'h30').replace(':00', 'h');
};

const InfosPratiques = () => {
  const [hours, setHours] = useState(DEFAULT_HOURS);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API}/settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.hours && data.hours.length > 0) {
            setHours(data.hours);
          }
        }
      } catch {
        // Keep fallback
      }
    };
    fetchSettings();
  }, []);

  const handleCall = () => {
    window.location.href = 'tel:+33978802623';
  };

  const handleDirections = () => {
    window.open('https://www.google.com/maps/search/?api=1&query=1+Place+du+18+Novembre+1944,+90600+Grandvillars', '_blank');
  };

  return (
    <main className="min-h-screen">
      <div className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-16">
          <div className="container mx-auto text-center transition-all duration-700 opacity-100 translate-y-0">
            <h1 className="heading-xl mb-6 text-gray-900 animate-fade-in-text animation-delay-0">
              Infos pratiques
            </h1>
            <p className="text-lead text-gray-700 max-w-2xl mx-auto mb-8 animate-fade-in-text animation-delay-150">
              Toutes les informations pour nous trouver, nous contacter et planifier votre visite chez IL TRULLO.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleCall} className="btn btn-secondary btn-lg">
                <Phone className="w-5 h-5" />
                Nous appeler
              </button>
              <button onClick={handleDirections} className="btn btn-primary btn-lg">
                <Navigation className="w-5 h-5" />
                Itinéraire GPS
              </button>
            </div>
          </div>
        </section>

        {/* Contact & Location Section */}
        <section className="section bg-white">
          <div className="container mx-auto transition-all duration-700 opacity-100 translate-y-0">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="heading-lg mb-8 text-gray-900">Nous contacter</h2>
                <div className="space-y-6">
                  {/* Phone */}
                  <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
                    <div className="flex items-center gap-4">
                      <Phone className="w-8 h-8 text-red-600" />
                      <div>
                        <h3 className="heading-sm text-red-800 mb-2">Téléphone</h3>
                        <button onClick={handleCall} className="text-xl font-bold text-red-700 hover:text-red-800 transition-colors">
                          +33 9 78 80 26 23
                        </button>
                        <p className="text-sm text-red-600 mt-1">Cliquez pour appeler directement</p>
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
                    <div className="flex items-start gap-4">
                      <MapPin className="w-8 h-8 text-green-600 mt-1" />
                      <div>
                        <h3 className="heading-sm text-green-800 mb-2">Adresse</h3>
                        <p className="text-lg font-semibold text-green-700 mb-2">
                          1 Place du 18 Novembre 1944, 90600 Grandvillars
                        </p>
                        <button onClick={handleDirections} className="btn btn-outline btn-sm text-green-700 border-green-600 hover:bg-green-600 hover:text-white">
                          <Navigation className="w-4 h-4" />
                          Ouvrir dans Maps
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
                    <div className="flex items-start gap-4">
                      <Clock className="w-8 h-8 text-blue-600 mt-1" />
                      <div className="w-full">
                        <h3 className="heading-sm text-blue-800 mb-4">Horaires d'ouverture</h3>
                        <div className="space-y-3">
                          {hours.map((h) => {
                            const fmt = (t) => t ? t.replace(':30', 'h30').replace(':00', 'h') : '';
                            return (
                              <div key={h.day} className="flex justify-between items-start">
                                <span className="font-medium text-blue-800">{h.day}</span>
                                {h.isOpen ? (
                                  <div className="text-right">
                                    {h.hasBreak && h.breakStart && h.breakEnd ? (
                                      <div className="font-bold text-green-600 text-sm space-y-0.5">
                                        <div>{fmt(h.open)} – {fmt(h.breakStart)}</div>
                                        <div>{fmt(h.breakEnd)} – {fmt(h.close)}</div>
                                      </div>
                                    ) : (
                                      <span className="font-bold text-green-600">
                                        {fmt(h.open)}–{fmt(h.close)}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="font-bold text-red-600">Fermé</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-blue-600 mt-3">* Horaires susceptibles d'évoluer selon les saisons</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div>
                <h2 className="heading-lg mb-8 text-gray-900">Nous trouver</h2>
                <div className="relative rounded-lg overflow-hidden shadow-md mb-6" style={{ height: '384px' }}>
                  <iframe
                    title="IL TRULLO sur Google Maps"
                    src="https://maps.google.com/maps?q=1+Place+du+18+Novembre+1944,+90600+Grandvillars,+France&t=&z=15&ie=UTF8&iwloc=&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>

                {/* Services */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="heading-sm mb-4 text-gray-900">Accès et services</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <Car className="w-5 h-5 text-green-600" />
                      <span className="text-body text-gray-700">Parking gratuit</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-purple-600" />
                      <span className="text-body text-gray-700">Accueil personnalisé</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Info className="w-5 h-5 text-orange-600" />
                      <span className="text-body text-gray-700">Conseils dégustation</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Payment Methods Section */}
        <section className="section bg-white">
          <div className="container mx-auto transition-all duration-700 opacity-100 translate-y-0">
            <div className="max-w-2xl mx-auto">
              <div className="bg-green-50 p-8 rounded-lg">
                <h3 className="heading-sm mb-6 text-green-900">Moyens de paiement acceptés</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-body text-green-800">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span>Espèces</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span>Carte bancaire</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <span>Carte restaurant</span>
                  </div>
                </div>
                <p className="text-sm text-green-700 mt-4 text-center">Paiement par carte sans minimum</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section bg-gray-50">
          <div className="container mx-auto transition-all duration-700 opacity-100 translate-y-0">
            <div className="text-center mb-12">
              <h2 className="heading-lg mb-4">Questions fréquentes</h2>
              <p className="text-lead text-gray-600">Peut-être trouverez-vous rapidement votre réponse ici</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="card">
                  <div className="card-body">
                    <h3 className="font-semibold text-gray-900 mb-2">Puis-je commander des pâtes fraîches à l'avance ?</h3>
                    <p className="text-gray-600">
                      Oui, nous recommandons même de commander pour les grosses quantités. Nos productions ont lieu le mardi et vendredi.
                    </p>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body">
                    <h3 className="font-semibold text-gray-900 mb-2">Préparez-vous des plateaux pour les événements ?</h3>
                    <p className="text-gray-600">
                      Absolument ! Nous préparons des plateaux antipasti, fromages et charcuteries sur commande pour vos réceptions et événements.
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="card">
                  <div className="card-body">
                    <h3 className="font-semibold text-gray-900 mb-2">Puis-je déguster avant d'acheter ?</h3>
                    <p className="text-gray-600">
                      Bien sûr ! Nous proposons régulièrement des dégustations de certains de nos produits. N'hésitez pas à demander lors de votre visite.
                    </p>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body">
                    <h3 className="font-semibold text-gray-900 mb-2">Faites-vous de la livraison ?</h3>
                    <p className="text-gray-600">
                      Actuellement, nous ne proposons que la vente en boutique. Un service de commande en ligne est prévu prochainement !
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section bg-gradient-to-r from-green-700 to-green-600 text-white">
          <div className="container mx-auto text-center">
            <h2 className="heading-lg mb-6">Une question ? Besoin d'un renseignement ?</h2>
            <p className="text-lead mb-8 opacity-90 max-w-2xl mx-auto">
              Notre équipe est à votre disposition pour vous renseigner et vous accueillir dans les meilleures conditions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={handleCall} className="btn btn-secondary btn-lg">
                <Phone className="w-5 h-5" />
                Appelez-nous : +33 9 78 80 26 23
              </button>
              <Link to="/contact" className="btn bg-white text-green-700 hover:bg-gray-100 btn-lg">
                <ArrowRight className="w-5 h-5" />
                Formulaire de contact
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default InfosPratiques;
